/*
 * <ia-countdown> — live countdown to a wall-clock deadline in a named
 * IANA timezone (DST-safe). Ticks the [data-days/hours/minutes/seconds]
 * children every second; the server renders "00" placeholders and the
 * element stays `hidden` until a valid future deadline is confirmed.
 *
 * data-deadline  "YYYY-MM-DD HH:MM(:SS)" wall-clock time in data-timezone
 * data-timezone  IANA zone name (default America/New_York)
 * data-expiry    behavior once the deadline passes:
 *                "hide"    hide closest [data-countdown-hide], or self (default)
 *                "section" hide the enclosing .shopify-section
 *                "text"    show [data-countdown-expired], hide the timer strip
 *
 * [data-countdown-embed] elements (inside the element or its
 * [data-countdown-hide] root) are hidden on expiry, so a promo embed
 * (e.g. a Klaviyo form) never outlives the deadline. Inside the theme
 * editor the element always stays visible so it remains editable.
 */
class IaCountdown extends HTMLElement {
  connectedCallback() {
    this.timerEl = this.querySelector('.ia-countdown');
    this.unitEls = {
      days: this.querySelector('[data-days]'),
      hours: this.querySelector('[data-hours]'),
      minutes: this.querySelector('[data-minutes]'),
      seconds: this.querySelector('[data-seconds]'),
    };
    this.endTime = IaCountdown.toEpoch(this.dataset.deadline, this.dataset.timezone || 'America/New_York');
    this.render();
  }

  disconnectedCallback() {
    this.stop();
  }

  get isDesignMode() {
    return this.hasAttribute('data-design-mode') || !!(window.Shopify && window.Shopify.designMode);
  }

  // block/card root that expiry "hide" removes (falls back to the element)
  get hideRoot() {
    return this.closest('[data-countdown-hide]') || this;
  }

  render() {
    this.stop();
    if (!this.endTime || this.endTime <= Date.now()) {
      if (this.isDesignMode) {
        this.reveal();
        return;
      }
      this.expire();
      return;
    }
    this.reveal();
    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  reveal() {
    this.removeAttribute('hidden');
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  tick() {
    const diff = this.endTime - Date.now();
    if (diff <= 0) {
      this.show(0, 0, 0, 0);
      this.stop();
      this.expire();
      return;
    }
    const sec = Math.floor(diff / 1000);
    this.show(
      Math.floor(sec / 86400),
      Math.floor((sec % 86400) / 3600),
      Math.floor((sec % 3600) / 60),
      sec % 60
    );
  }

  show(days, hours, minutes, seconds) {
    const values = { days, hours, minutes, seconds };
    Object.keys(values).forEach((key) => {
      const el = this.unitEls[key];
      if (el) el.textContent = String(values[key]).padStart(2, '0');
    });
  }

  expire() {
    if (this.isDesignMode) {
      this.reveal();
      return;
    }
    this.hideRoot.querySelectorAll('[data-countdown-embed]').forEach((embed) => {
      embed.hidden = true;
    });
    const expired = this.querySelector('[data-countdown-expired]');
    if (this.dataset.expiry === 'text' && expired) {
      this.reveal();
      if (this.timerEl) this.timerEl.hidden = true;
      expired.hidden = false;
      return;
    }
    const target = this.dataset.expiry === 'section' ? this.closest('.shopify-section') || this : this.hideRoot;
    target.style.display = 'none';
  }

  // ms that `tz` is ahead of UTC at the given instant
  static zoneOffset(epoch, tz) {
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const parts = {};
      dtf.formatToParts(new Date(epoch)).forEach((part) => {
        if (part.type !== 'literal') parts[part.type] = part.value;
      });
      const asUTC = Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second)
      );
      return asUTC - epoch;
    } catch (error) {
      return 0; // unknown timezone -> treat the deadline as UTC
    }
  }

  // wall-clock "YYYY-MM-DD HH:MM(:SS)" in `tz` -> epoch ms (0 if blank/invalid).
  // Two-pass offset lookup so deadlines around DST switches resolve correctly.
  static toEpoch(value, tz) {
    if (!value) return 0;
    const match = String(value)
      .trim()
      .match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return 0;
    const guess = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
    if (Number.isNaN(guess)) return 0;
    const epoch = guess - IaCountdown.zoneOffset(guess, tz);
    return guess - IaCountdown.zoneOffset(epoch, tz);
  }
}

if (!customElements.get('ia-countdown')) {
  customElements.define('ia-countdown', IaCountdown);
}

export default IaCountdown;
